import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import AdamChat from './AdamChat';
import AdamWelcome from './AdamWelcome';
import AdamChatSidebar from './AdamChatSidebar';
import travelAgentService from '../../services/travelAgentService';
import hotelService from '../../services/hotelService';
import { getChatById, getActiveChat } from '../../services/adamConciergeService';
import ConciergeHotelRates from '../../components/ConciergeHotelRates';

const AdamConciergePage = () => {
    const navigate = useNavigate();
    const [activeHotels, setActiveHotels] = useState([]);
    const [activeExperiences, setActiveExperiences] = useState([]);
    const [searchParams, setSearchParams] = useState({
        checkInDate: null,
        checkOutDate: null,
        adults: 2,
        children: 0,
        currency: 'USD' // Always USD
    });
    const activeHotelsRef = useRef([]);
    const [hotelsEmissions, setHotelsEmissions] = useState({});
    const [loadingEmissions, setLoadingEmissions] = useState({});

    const user = travelAgentService.getCurrentUser();
    const agentId = user?._id || user?.id;
    const agentName = user?.firstName || user?.name || 'Agent';

    // Chat sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeChatId, setActiveChatId] = useState(null);
    const [chatMessages, setChatMessages] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // User dropdown state
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
    const userDropdownRef = useRef(null);
    const userAvatarRef = useRef(null);

    // Which hotel cards have "View rates & add to cart" expanded (full-width layout)
    const [expandedHotelKeys, setExpandedHotelKeys] = useState(() => new Set());

    // Keep ref in sync
    useEffect(() => {
        activeHotelsRef.current = activeHotels;
    }, [activeHotels]);

    // Load active chat on mount (e.g. after refresh)
    useEffect(() => {
        const loadActiveChat = async () => {
            if (agentId) {
                try {
                    const response = await getActiveChat(agentId);
                    if (response.success && response.data) {
                        setActiveChatId(response.data._id);
                        if (response.data.messages && response.data.messages.length > 0) {
                            setChatMessages(response.data.messages);
                            // Restore search params from last message that has them so hotel links keep check-in/out after refresh
                            const msgWithParams = [...response.data.messages].reverse().find(m => m.searchParams && (m.searchParams.checkInDate || m.searchParams.checkOutDate));
                            if (msgWithParams?.searchParams) {
                                setSearchParams(prev => ({
                                    checkInDate: msgWithParams.searchParams.checkInDate || prev.checkInDate,
                                    checkOutDate: msgWithParams.searchParams.checkOutDate || prev.checkOutDate,
                                    adults: msgWithParams.searchParams.adults ?? prev.adults,
                                    children: msgWithParams.searchParams.children ?? prev.children,
                                    currency: msgWithParams.searchParams.currency || 'USD'
                                }));
                            }
                        }
                    }
                } catch (error) {
                    // Error loading active chat
                }
            }
        };
        loadActiveChat();
    }, [agentId]);

    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };

    // User dropdown
    const toggleUserDropdown = () => {
        if (!isUserDropdownOpen && userAvatarRef.current) {
            const rect = userAvatarRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 8,
                right: window.innerWidth - rect.right
            });
        }
        setIsUserDropdownOpen(prev => !prev);
    };

    const handleLogout = useCallback(() => {
        travelAgentService.logout();
        navigate('/login', { replace: true });
    }, [navigate]);

    const handleNavigation = (path) => {
        setIsUserDropdownOpen(false);
        navigate(path);
    };

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isUserDropdownOpen &&
                userDropdownRef.current &&
                userAvatarRef.current &&
                !userDropdownRef.current.contains(event.target) &&
                !userAvatarRef.current.contains(event.target)
            ) {
                setIsUserDropdownOpen(false);
            }
        };

        if (isUserDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isUserDropdownOpen]);

    // Hotels found callback
    const handleHotelsFound = useCallback((hotels, params = null) => {
        if (hotels && hotels.length > 0) {
            // Filter out hotels without images or prices
            const validHotels = hotels.filter(hotel => {
                const hasImage = hotel.imageUrl && hotel.imageUrl.trim() !== '';
                const hasPrice = hotel.lowestRate !== null && hotel.lowestRate !== undefined && hotel.lowestRate > 0;
                return hasImage && hasPrice;
            });
            
            if (validHotels.length > 0) {
                setActiveHotels(validHotels);
                activeHotelsRef.current = validHotels;
                
                // Update search parameters if provided
                if (params) {
                    setSearchParams(prevParams => ({
                        checkInDate: params.checkInDate || prevParams.checkInDate,
                        checkOutDate: params.checkOutDate || prevParams.checkOutDate,
                        adults: params.adults || prevParams.adults,
                        children: params.children || prevParams.children,
                        currency: 'USD' // Always USD
                    }));
                }
            } else {
                setActiveHotels([]);
                activeHotelsRef.current = [];
            }
        }
    }, []);

    // Experiences found callback
    const handleExperiencesFound = useCallback((experiences) => {
        if (experiences && experiences.length > 0) {
            setActiveExperiences(experiences);
        }
    }, []);

    const handleNewChat = useCallback((chatId, chatData) => {
        setActiveHotels([]);
        setActiveExperiences([]);
        activeHotelsRef.current = [];
        if (chatId) {
            setActiveChatId(chatId);
            setChatMessages([]);
        }
    }, []);

    const handleSelectChat = useCallback(async (chatId) => {
        if (!agentId) return;
        try {
            const response = await getChatById(chatId, agentId);
            if (response.success && response.data) {
                setActiveChatId(chatId);
                setChatMessages(response.data.messages || []);
                setActiveHotels([]);
                setActiveExperiences([]);
                activeHotelsRef.current = [];

                // Extract hotels from messages
                const hotelMessages = response.data.messages?.filter(m => m.type === 'hotels' || (m.type === 'text' && m.hotels?.length > 0));
                if (hotelMessages?.length > 0) {
                    const lastHotelMsg = hotelMessages[hotelMessages.length - 1];
                    if (lastHotelMsg.hotels?.length > 0) {
                        // Filter hotels to only include those with images and prices
                        const validHotels = lastHotelMsg.hotels.filter(hotel => {
                            const hasImage = hotel.imageUrl && hotel.imageUrl.trim() !== '';
                            const hasPrice = hotel.lowestRate !== null && hotel.lowestRate !== undefined && hotel.lowestRate > 0;
                            return hasImage && hasPrice;
                        });
                        
                        if (validHotels.length > 0) {
                            setActiveHotels(validHotels);
                            activeHotelsRef.current = validHotels;
                        }
                    }
                }

                // Extract experiences
                const expMessages = response.data.messages?.filter(m => m.type === 'experiences');
                if (expMessages?.length > 0) {
                    const lastExpMsg = expMessages[expMessages.length - 1];
                    if (lastExpMsg.experiences?.length > 0) {
                        setActiveExperiences(lastExpMsg.experiences);
                    }
                }

                // Restore search params from last message that has them (so hotel links keep check-in/out dates)
                const msgWithParams = (response.data.messages || []).slice().reverse().find(m => m.searchParams && (m.searchParams.checkInDate || m.searchParams.checkOutDate));
                if (msgWithParams?.searchParams) {
                    setSearchParams(prev => ({
                        checkInDate: msgWithParams.searchParams.checkInDate || prev.checkInDate,
                        checkOutDate: msgWithParams.searchParams.checkOutDate || prev.checkOutDate,
                        adults: msgWithParams.searchParams.adults ?? prev.adults,
                        children: msgWithParams.searchParams.children ?? prev.children,
                        currency: msgWithParams.searchParams.currency || 'USD'
                    }));
                }
            }
        } catch (error) {
            // Error loading chat
        }
    }, [agentId]);

    const toggleSidebar = useCallback(() => {
        setSidebarOpen(prev => !prev);
    }, []);

    const handleChatTitleUpdate = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    // Filter hotels to only show those with images and prices
    const displayHotels = activeHotels.filter(hotel => {
        const hasImage = hotel.imageUrl && hotel.imageUrl.trim() !== '';
        const hasPrice = hotel.lowestRate !== null && hotel.lowestRate !== undefined && hotel.lowestRate > 0;
        return hasImage && hasPrice;
    });
    const displayExperiences = activeExperiences;
    const hasResults = displayHotels.length > 0 || displayExperiences.length > 0;

    const getCarbonTierColor = (tierName) => {
        if (!tierName || typeof tierName !== 'string') return 'bg-gray-200 text-gray-700';
        const tier = tierName.toLowerCase();
        if (tier.includes('low') || tier.includes('green')) return 'bg-green-100 text-green-800';
        if (tier.includes('medium') || tier.includes('moderate')) return 'bg-yellow-100 text-yellow-800';
        if (tier.includes('high') || tier.includes('impact')) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const formatEmissions = (value) => {
        if (value === undefined || value === null) return 'N/A';
        if (value < 1) return value.toFixed(3);
        if (value < 10) return value.toFixed(2);
        if (value < 100) return value.toFixed(1);
        return Math.round(value);
    };

    const fetchHotelEmissions = useCallback(async (hotel, hotelKey) => {
        const countryName = hotel.countryName || hotel.country || '';
        const cityName = hotel.city || '';
        if (!countryName && !cityName) return;

        setLoadingEmissions(prev => ({ ...prev, [hotelKey]: true }));
        try {
            const hotelLocation = {
                country: countryName,
                state: cityName,
                countryCode: hotel.countryCode || '',
            };
            const emissionsData = await hotelService.previewEmissions({
                hotelLocation,
                amenitiesList: [],
                ecoCertified: false,
                occupancy: 1,
                roomType: 'standard',
                selectedRoomType: null,
                numberOfNights: 1,
                numberOfRooms: 1,
            });
            setHotelsEmissions(prev => ({ ...prev, [hotelKey]: emissionsData }));
        } catch (error) {
            setHotelsEmissions(prev => ({ ...prev, [hotelKey]: null }));
        } finally {
            setLoadingEmissions(prev => ({ ...prev, [hotelKey]: false }));
        }
    }, []);

    const displayHotelKeys = displayHotels.map(h => h.hotelCode || h.hotelcode).filter(Boolean).join(',');
    useEffect(() => {
        if (displayHotels.length === 0) return;
        displayHotels.forEach((hotel, index) => {
            const hotelKey = hotel.hotelCode || hotel.hotelcode || `hotel_${index}`;
            fetchHotelEmissions(hotel, hotelKey);
        });
    }, [displayHotelKeys, fetchHotelEmissions]);

    return (
        <div className="h-[100dvh] max-h-[100dvh] w-full min-w-0 bg-white text-gray-900 overflow-hidden flex flex-col">
            {/* Chat Sidebar */}
            <AdamChatSidebar
                isOpen={sidebarOpen}
                onToggle={toggleSidebar}
                agentId={agentId}
                activeChatId={activeChatId}
                onSelectChat={handleSelectChat}
                onNewChat={handleNewChat}
                refreshTrigger={refreshTrigger}
            />

            {/* Header */}
            <header className={`flex-none h-11 md:h-12 px-2 sm:px-3 md:px-5 flex items-center justify-between gap-2 border-b border-gray-100 bg-white/90 backdrop-blur-sm z-[100] transition-all duration-300 min-w-0 ${sidebarOpen ? 'md:ml-72' : ''}`}>
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <button
                        onClick={handleBackToDashboard}
                        className="p-1.5 rounded-full hover:bg-gray-50 transition-all duration-300 text-gray-500 hover:text-gray-900"
                        title="Back to Dashboard"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <h1 className="text-base sm:text-lg md:text-xl font-semibold tracking-wider text-gray-900 truncate">
                            SEREN
                        </h1>
                        <span className="shrink-0 px-1.5 py-0.5 text-[8px] sm:text-[9px] border border-gray-200 rounded-full uppercase tracking-widest text-gray-500 font-light">
                            Concierge
                        </span>
                    </div>
                </div>

                {/* Right side */}
                <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                    {/* Nav links */}
                    <div className="hidden md:flex items-center gap-6">
                        <button
                            type="button"
                            onClick={() => navigate('/experiences')}
                            className="text-xs font-medium uppercase tracking-widest text-gray-700 hover:text-gray-900 transition-colors"
                        >
                            Experiences
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/hotels')}
                            className="text-xs font-medium uppercase tracking-widest text-gray-700 hover:text-gray-900 transition-colors"
                        >
                            Hotels
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/private-jets')}
                            className="text-xs font-medium uppercase tracking-widest text-gray-700 hover:text-gray-900 transition-colors"
                        >
                            Private Jets
                        </button>
                    </div>

                    {/* User avatar */}
                    <div className="relative flex items-center gap-2">
                        <div
                            ref={userAvatarRef}
                            onClick={toggleUserDropdown}
                            className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                        >
                            <span className="text-[10px] font-semibold">
                                {agentName ? agentName[0].toUpperCase() : 'A'}
                            </span>
                        </div>

                        <AnimatePresence>
                            {isUserDropdownOpen && (
                                <motion.div
                                    ref={userDropdownRef}
                                    className="fixed bg-white border border-gray-200 rounded-lg shadow-xl w-48"
                                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                    transition={{ duration: 0.2 }}
                                    style={{
                                        position: 'fixed',
                                        top: `${dropdownPosition.top}px`,
                                        right: `${dropdownPosition.right}px`,
                                        zIndex: 99999
                                    }}
                                >
                                    <div className="p-4">
                                        <h2 className="text-sm font-semibold text-gray-800 mb-3">
                                            Hi, {agentName}!
                                        </h2>
                                        <hr className="border-gray-200 mb-3" />
                                        <ul className="space-y-1">
                                            <li>
                                                <button
                                                    onClick={() => handleNavigation('/dashboard')}
                                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors font-medium"
                                                >
                                                    Dashboard
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() => handleNavigation('/profile')}
                                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors font-medium"
                                                >
                                                    Profile
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() => handleNavigation('/clients')}
                                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors font-medium"
                                                >
                                                    Clients
                                                </button>
                                            </li>
                                            <hr className="border-gray-200 my-2" />
                                            <li>
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors font-light"
                                                >
                                                    Sign Out
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </header>

            {/* Main Split Layout — flex ratios on mobile replace % heights so chat + panel scroll correctly */}
            <div className={`flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden overflow-x-hidden transition-all duration-300 ${sidebarOpen ? 'md:ml-72' : ''}`}>
                {/* Left Side - Chat — full height on mobile when welcome panel is hidden */}
                <div className={`w-full md:w-1/2 min-h-0 md:flex-none md:h-full bg-white border-r-0 md:border-r border-b md:border-b-0 border-gray-100 flex flex-col relative z-0 ${hasResults ? 'flex-[3]' : 'flex-1'}`}>
                    <AdamChat
                        onHotelsFound={handleHotelsFound}
                        onExperiencesFound={handleExperiencesFound}
                        onNewChat={handleNewChat}
                        activeChatId={activeChatId}
                        initialMessages={chatMessages}
                        agentId={agentId}
                        agentName={agentName}
                        sidebarOpen={sidebarOpen}
                        onToggleSidebar={toggleSidebar}
                        onChatTitleUpdate={handleChatTitleUpdate}
                        fallbackSearchParams={searchParams}
                    />
                </div>

                {/* Right Side — results (always on mobile when present); welcome panel desktop-only */}
                <div className={`w-full md:w-1/2 min-h-0 flex-[2] md:flex-none md:h-full bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden flex-col ${hasResults ? 'flex' : 'hidden md:flex'}`}>
                    {/* Decorative BG */}
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-black/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-80 h-80 bg-black/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
                    </div>

                    {hasResults ? (
                        <div className="relative h-full flex flex-col bg-gray-50/50">
                            {/* Header */}
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="flex-none px-4 md:px-6 pt-5 pb-4"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
                                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                        <div className="w-1 h-1 rounded-full bg-black shrink-0" />
                                        <span className="text-[10px] sm:text-xs font-medium tracking-[0.15em] sm:tracking-[0.2em] uppercase text-gray-900 truncate">
                                            {displayExperiences.length > 0 ? 'Curated Experiences' : 'Hotel Collection'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-500 shrink-0">
                                        <span className="text-[10px] font-light tracking-wide whitespace-nowrap">
                                            {displayExperiences.length > 0
                                                ? `${displayExperiences.length} ${displayExperiences.length === 1 ? 'Experience' : 'Experiences'}`
                                                : `${displayHotels.length} ${displayHotels.length === 1 ? 'Hotel' : 'Hotels'}`
                                            }
                                        </span>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Scrollable Grid */}
                            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 sm:px-4 md:px-6 pb-4 sm:pb-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={`${displayHotels.map(h => h.hotelName).join(',')}-${displayExperiences.map(e => e.title).join(',')}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0 [&>*]:min-w-0"
                                    >
                                        {/* Curated Experiences first */}
                                        {displayExperiences.map((exp, idx) => (
                                            <motion.div
                                                key={`exp-${exp.title}-${idx}`}
                                                initial={{ opacity: 0, y: 30 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.1, duration: 0.6 }}
                                                className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer border border-gray-100"
                                                onClick={() => {
                                                    const expId = exp.id || exp._id;
                                                    if (expId) window.open(`/experience/${expId}`, '_blank', 'noopener,noreferrer');
                                                }}
                                            >
                                                <div className="relative h-44 overflow-hidden">
                                                    <img
                                                        src={exp.coverImage || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=600&fit=crop'}
                                                        alt={exp.title}
                                                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
                                                    {exp.category && (
                                                        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md rounded-full px-2 py-1">
                                                            <span className="text-[9px] font-medium tracking-wider uppercase text-gray-900">{exp.category}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="p-4 space-y-3">
                                                    <h3 className="text-base typography-card-title text-gray-900 leading-tight">{exp.title}</h3>
                                                    <div className="flex items-center gap-1.5 text-gray-500">
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        </svg>
                                                        <span className="text-[10px] font-light">
                                                            {exp.location?.city}{exp.location?.country ? `, ${exp.location.country}` : ''}
                                                        </span>
                                                    </div>
                                                    {(exp.price != null || exp.listingTotal != null) && (
                                                        <div className="pt-2 border-t border-gray-100">
                                                            <span className="text-sm font-medium text-gray-900">
                                                                {exp.currency === 'GBP' ? '£' : exp.currency === 'USD' ? '$' : '€'}
                                                                {(exp.listingTotal ?? exp.price ?? ((exp.basePrice || 0) + (exp.commissionAmount || 0))).toLocaleString()}
                                                            </span>
                                                            <span className="text-xs text-gray-400 ml-1">/person</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        ))}
                                        {/* Recommended Hotels (after Curated Experiences when both present) */}
                                        {displayHotels.length > 0 && displayExperiences.length > 0 && (
                                            <div className="lg:col-span-2 flex items-center gap-2 mt-6 mb-2">
                                                <div className="w-1 h-1 rounded-full bg-black" />
                                                <span className="text-xs font-medium tracking-[0.2em] uppercase text-gray-700">
                                                    Hotel Collection
                                                </span>
                                                <span className="text-[10px] font-light text-gray-500">
                                                    {displayHotels.length} {displayHotels.length === 1 ? 'Hotel' : 'Hotels'}
                                                </span>
                                            </div>
                                        )}
                                        {displayHotels.map((hotel, idx) => {
                                            const hotelCode = hotel.hotelcode || hotel.hotelCode || hotel.sabreHotelCode;
                                            const cardKey = `hotel-${idx}-${hotelCode}`;
                                            const isRatesExpanded = expandedHotelKeys.has(cardKey);
                                            return (
                                            <motion.div
                                                key={`hotel-${hotel.hotelName}-${idx}`}
                                                initial={{ opacity: 0, y: 30 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: (displayExperiences.length + idx) * 0.1, type: 'spring', stiffness: 300, damping: 28 }}
                                                whileHover={{ y: -4, boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
                                                className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm cursor-pointer border border-gray-100 transition-shadow duration-300 ${isRatesExpanded ? 'lg:col-span-2' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (hotelCode) {
                                                        // Build URL with search parameters - only include non-empty values
                                                        const params = new URLSearchParams();
                                                        if (searchParams.checkInDate) {
                                                            params.set('checkInDate', searchParams.checkInDate);
                                                        }
                                                        if (searchParams.checkOutDate) {
                                                            params.set('checkOutDate', searchParams.checkOutDate);
                                                        }
                                                        params.set('adults', searchParams.adults?.toString() || '2');
                                                        params.set('children', searchParams.children?.toString() || '0');
                                                        params.set('currency', 'USD'); // Always USD
                                                        
                                                        const hotelUrl = `/hotel/${hotelCode}?${params.toString()}`;
                                                        window.open(hotelUrl, '_blank', 'noopener,noreferrer');
                                                    }
                                                }}
                                            >
                                                <div className="relative h-44 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                                                    {hotel.imageUrl ? (
                                                        <img
                                                            src={hotel.imageUrl}
                                                            alt={hotel.hotelName}
                                                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                                            loading="lazy"
                                                            onError={(e) => { 
                                                                e.target.onerror = null; 
                                                                e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop&q=80';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                                                            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

                                                    {/* Hotel category badge (Recommended / Compass) */}
                                                    {hotel.category && hotel.category !== 'No Chain Code' && (
                                                        <div className="absolute top-3 right-3 px-3 py-1.5 text-xs font-semibold shadow-lg bg-slate-900 text-white rounded-full flex items-center gap-1.5">
                                                            {hotel.category === 'Recommended' && (
                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                                </svg>
                                                            )}
                                                            {hotel.category === 'Compass' && (
                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                                </svg>
                                                            )}
                                                            <span>{hotel.category}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="p-4 space-y-3">
                                                    {isRatesExpanded ? (
                                                        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                                                            <div className="min-w-0 flex-1 space-y-1.5">
                                                                <h3 className="text-base typography-card-title text-gray-900 group-hover:text-black transition-colors leading-tight">
                                                                    {hotel.hotelName}
                                                                </h3>
                                                                <div className="flex items-center gap-1.5 text-gray-500">
                                                                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    </svg>
                                                                    <span className="text-[10px] font-light tracking-wide truncate">
                                                                        {hotel.location || (hotel.city && hotel.countryName ? `${hotel.city}, ${hotel.countryName}` : hotel.city || 'Location unavailable')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="shrink-0 flex flex-col items-start sm:items-end text-left sm:text-right space-y-1.5 w-full sm:w-auto">
                                                                {hotel.lowestRate && (
                                                                    <div>
                                                                        <span className="text-sm font-medium text-gray-900">
                                                                            USD {hotel.lowestRate}
                                                                        </span>
                                                                        <span className="text-xs text-gray-400 ml-1">/night</span>
                                                                    </div>
                                                                )}
                                                                {(() => {
                                                                    const hotelKey = hotel.hotelCode || hotel.hotelcode || `hotel_${idx}`;
                                                                    const emissionsData = hotelsEmissions[hotelKey];
                                                                    const isLoading = loadingEmissions[hotelKey];
                                                                    if (isLoading) {
                                                                        return (
                                                                            <div className="pt-1.5 flex items-center gap-2">
                                                                                <span className="text-[10px] font-medium text-gray-500">Environmental Impact</span>
                                                                                <div className="w-16 h-4 bg-gray-100 rounded animate-pulse" />
                                                                            </div>
                                                                        );
                                                                    }
                                                                    if (emissionsData?.data) {
                                                                        const emissions = emissionsData.data;
                                                                        const emissionValue = emissions.carbonEmission ?? null;
                                                                        if (emissionValue == null) return null;
                                                                        return (
                                                                            <div className="pt-1.5 flex items-center gap-1.5 flex-wrap">
                                                                                <span className="text-[10px] font-medium text-gray-500">Environmental Impact</span>
                                                                                <span className="text-xs text-gray-700">
                                                                                    <span className="font-semibold">{formatEmissions(emissionValue)}</span>
                                                                                    <span className="text-gray-500 ml-0.5">kg CO₂e</span>
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })()}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="space-y-1.5">
                                                                <h3 className="text-base typography-card-title text-gray-900 group-hover:text-black transition-colors leading-tight">
                                                                    {hotel.hotelName}
                                                                </h3>
                                                                <div className="flex items-center gap-1.5 text-gray-500">
                                                                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    </svg>
                                                                    <span className="text-[10px] font-light tracking-wide truncate">
                                                                        {hotel.location || (hotel.city && hotel.countryName ? `${hotel.city}, ${hotel.countryName}` : hotel.city || 'Location unavailable')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            {hotel.lowestRate && (
                                                                <div className="pt-2 border-t border-gray-100">
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        USD {hotel.lowestRate}
                                                                    </span>
                                                                    <span className="text-xs text-gray-400 ml-1">/night</span>
                                                                </div>
                                                            )}
                                                            {(() => {
                                                                const hotelKey = hotel.hotelCode || hotel.hotelcode || `hotel_${idx}`;
                                                                const emissionsData = hotelsEmissions[hotelKey];
                                                                const isLoading = loadingEmissions[hotelKey];
                                                                if (isLoading) {
                                                                    return (
                                                                        <div className="pt-2 border-t border-gray-100 flex items-center gap-2">
                                                                            <span className="text-[10px] font-medium text-gray-500">Environmental Impact</span>
                                                                            <div className="w-16 h-4 bg-gray-100 rounded animate-pulse" />
                                                                        </div>
                                                                    );
                                                                }
                                                                if (emissionsData?.data) {
                                                                    const emissions = emissionsData.data;
                                                                    const emissionValue = emissions.carbonEmission ?? null;
                                                                    if (emissionValue == null) return null;
                                                                    return (
                                                                        <div className="pt-2 border-t border-gray-100 flex items-center gap-1.5 flex-wrap">
                                                                            <span className="text-[10px] font-medium text-gray-500">Environmental Impact</span>
                                                                            <span className="text-xs text-gray-700">
                                                                                <span className="font-semibold">{formatEmissions(emissionValue)}</span>
                                                                                <span className="text-gray-500 ml-0.5">kg CO₂e</span>
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </>
                                                    )}
                                                    <div onClick={(e) => e.stopPropagation()} className="pt-2 border-t border-gray-100">
                                                        <ConciergeHotelRates
                                                            hotel={{
                                                                hotelCode: hotelCode,
                                                                hotelName: hotel.hotelName,
                                                                imageUrl: hotel.imageUrl
                                                            }}
                                                            searchParams={{
                                                                checkInDate: searchParams.checkInDate,
                                                                checkOutDate: searchParams.checkOutDate,
                                                                adults: searchParams.adults ?? 2,
                                                                children: searchParams.children ?? 0,
                                                                currency: searchParams.currency || 'USD'
                                                            }}
                                                            cardKey={cardKey}
                                                            onExpandChange={(expanded, key) => {
                                                                setExpandedHotelKeys(prev => {
                                                                    const next = new Set(prev);
                                                                    if (expanded) next.add(key);
                                                                    else next.delete(key);
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="flex items-center justify-end pt-2">
                                                        <motion.div
                                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-900 text-gray-900 text-xs font-semibold uppercase tracking-wider hover:bg-gray-900 hover:text-white transition-colors cursor-pointer group/btn"
                                                            whileHover={{ scale: 1.03, x: 2 }}
                                                            whileTap={{ scale: 0.98 }}
                                                            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                                        >
                                                            <span>View Details</span>
                                                            <motion.span
                                                                className="inline-flex"
                                                                initial={false}
                                                                animate={{ x: 0 }}
                                                                whileHover={{ x: 3 }}
                                                                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                                </svg>
                                                            </motion.span>
                                                        </motion.div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ); })}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    ) : (
                        <AdamWelcome />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdamConciergePage;
